import FlowWizard from "@shared/components/form/FlowWizard";
import { StepType } from "@shared/components/form/types";
import { ActivityName, IQuestionnaireRequest } from "app/_modules/core/types";
import {
  IQuestionnaireQuestionRaw,
  QuestionnaireAnswer,
  QuestionnaireSubmitForm,
} from "app/_modules/core/questionnaire";
import {
  buildQuestionnaireAnswers,
  isCurrentQuestionValid,
  normalizeQuestionnaireQuestions,
} from "app/_modules/core/questionnaire";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { TNavigationStack } from "app/types";
import { usePost } from "app/queryProvider";
import { useAuth } from "app/auth/hooks/useAuth";
import { Loading } from "@shared/components/loading";
import { QuestionnaireSubmitStep } from "../steps";
import { ProgressBar } from "@md-design-system/ProgressBar";
import { colorPalette } from "@shared/config/colorsConfig";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";

type QuestionnaireWizardStepKey = `questionnaireStep_${number}`;
type QuestionnaireFlowData = Record<QuestionnaireWizardStepKey, QuestionnaireSubmitForm>;

const QuestionnaireFlow = ({ activityName, activityId, initialStep = 0, forceReset = false }: { activityName: ActivityName, activityId: string, initialStep?: number, forceReset?: boolean }) => {
  const [answersByQuestionId, setAnswersByQuestionId] = useState<Record<string, QuestionnaireAnswer>>({});
  const [currentStepIndex, setCurrentStepIndex] = useState(forceReset ? 0 : initialStep);
  const { meData } = useAuth()
  const navigation = useNavigation<NativeStackNavigationProp<TNavigationStack>>();
  const { t } = useTranslation();

  const telemonitoringPathwayId = meData?.current_pathway?.id || "";

  const questionnaireData = meData?.current_pathway?.questionnaires.find(
    (q) => q.questionnaire.name === activityName
  )?.questionnaire;

  const questionnaireQuestions = useMemo(() => {
    return normalizeQuestionnaireQuestions(
      questionnaireData?.questionnaire_questions as IQuestionnaireQuestionRaw[] | undefined
    );
  }, [questionnaireData]);

  useEffect(() => {
    setAnswersByQuestionId({});
    setCurrentStepIndex(forceReset ? 0 : initialStep);
  }, [activityId, forceReset, initialStep]);

  const { mutate: createQuestionnaire, isPending: isCreatingQuestionnaire } = usePost<IQuestionnaireRequest, undefined>({
    options: {
      onSuccess: () => {
        navigation.navigate("ActivitySuccess", { administered: true, activityId, activityType: 'questionnaire' });
      },
    },
  });


  const steps = useMemo(() => {
    return questionnaireQuestions.map((question, index) => {
      const key = `questionnaireStep_${index}` as QuestionnaireWizardStepKey;
      const answer = answersByQuestionId[question.id];

      return {
        component: QuestionnaireSubmitStep,
        key,
        context: {
          question,
          answer,
          showError: false,
          onAnswerChange: (questionId: string, nextAnswer: QuestionnaireAnswer) => {
            setAnswersByQuestionId((prev) => ({
              ...prev,
              [questionId]: nextAnswer,
            }));
          }
        },
        initialValues: {
          answer: answer || { value: null, text: null }
        },
        showBackButton: index > 0,
        isNextDisabled: !isCurrentQuestionValid(question, answer),
        isFinishDisabled: !isCurrentQuestionValid(question, answer),
      } as StepType<QuestionnaireWizardStepKey, QuestionnaireSubmitForm, any>;
    });
  }, [answersByQuestionId, questionnaireQuestions]);

  const staticTopContent = (
    <ProgressBar
      percentage={
        questionnaireQuestions.length > 0
          ? ((currentStepIndex + 1) / questionnaireQuestions.length) * 100
          : 0
      }
      trackColor={colorPalette.gray[200]}
      label={t("screens.activities.questionnaire.questionCounter", {
        current: Math.min(currentStepIndex + 1, questionnaireQuestions.length),
        total: questionnaireQuestions.length,
      })}
    />
  );

  const handleFinish = () => {
    if (!questionnaireQuestions.length) {
      return;
    }

    const hasInvalidMandatoryAnswer = questionnaireQuestions.some((question) => {
      return !isCurrentQuestionValid(question, answersByQuestionId[question.id]);
    });

    if (hasInvalidMandatoryAnswer) {
      return;
    }

    const answers = buildQuestionnaireAnswers(questionnaireQuestions, answersByQuestionId);

    createQuestionnaire({
      resource: { name: "pathways", id: telemonitoringPathwayId },
      url: `/activities/${activityId}/questionnaire-results`,
      data: { answers },
    });
  };

  if (isCreatingQuestionnaire) {
    return <Loading />
  }

  if (!questionnaireQuestions.length) {
    return null;
  }

  return (
    <FlowWizard<QuestionnaireFlowData>
      key={`${activityId}:${forceReset}:${initialStep}`}
      steps={steps}
      onFinish={handleFinish}
      onStepChange={(_, currentStep) => setCurrentStepIndex(currentStep)}
      animationType="scale"
      initialStep={forceReset ? 0 : initialStep}
      layout="horizontal"
      finishText="buttons.continue"
      staticTopContent={staticTopContent}
    />
  );
};


export default QuestionnaireFlow;
